import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import {
  Collapse,
  useCollapseState,
  ValidatedBootstrapPanel,
  ValidatedPanel,
  ValidatedFixedPanel,
  TextTitle,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { useWizardService } from '@bingads-webui-react/wizard';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { CampaignSubTypes, ScopeLevel } from '@bingads-webui-campaign-react/constants';
import { formatAudience, Device, formatDevices } from '@bingads-webui-campaign-react/xandr-campaign';
import { MessageBar, MessageBarBody } from '@fluentui/react-message-bar';
import { getAdScheduleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { getDateSettingDetails } from '@bingads-webui-campaign/date-setting';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ValidatedCustomSegmentsPicker, formatCustomSegment } from '@bingads-webui-campaign-react/custom-segments-edit-create';
import { BidStrategy } from '@bingads-webui-campaign/audience-campaign-shared';
import { formatInterests } from '@bingads-webui-campaign-react/audience-interests';
import { getUrlOptionsDetails } from '@bingads-webui-campaign-react/url-options';
import { ValidatedUrlOptionsParameter } from '@bingads-webui-campaign-react/validated-url-input';
import { ValidatedAdRotation, ROTATION_OPTION_TO_TEXT } from '@bingads-webui-react/ad-rotation';
import { ServerValidatedWebExclusions, webExclusionPropertyVal } from '@bingads-webui-react/web-exclusions';

import { SectionShimmer } from '../../../shared/section-shimmer';
import { getAdGroupStyles } from '../adgroup-styles';
import { ValidatedCompanyPicker, formatCompany } from './company-target';
import { ValidatedIndustryPicker, formatIndustry } from './industry-target';
import { ValidatedJobFunctionPicker, formatJobFunction } from './job-function-target';
import { PredictiveTargetingControlHeader } from './predictive-targeting-control';
import { ValidatedPlacementsFunctionPicker, formatPlacements } from './placements-function-target';
import { ContentTargetingPlacements, getContentTargetingPlacementsSummaryText } from './content-targeting-placements';
import { getAdGroupPanels } from '../../../step-utils';
import { AdSchedule } from './ad-schedule';
import { ValidatedTargetOrBidSelector } from './target-or-bid-selector';
import { ValidatedAudiencePickerWithBidAdjustment } from './audience-target';
import { ValidatedBidAdjustment } from '../components/audience-bid-adjustment';
import { ValidatedLevelSwitch } from './validated-level-switch';
import { ValidatedAudienceInterestsWithBidAdjustment } from './validated-audience-interests-with-bid-adjustment';
import { isAudience, isAudienceAdGroupEdit, isAdGroupCreation, isDisplayAdsCampaign, isCTVCampaign } from '../../../step-constants';
import { CampaignObjectiveType, DeviceTypes } from '../../../common/constants';

const getPanelErrorProps = ({ needsAttention }) => (needsAttention ? {
  error: _TL_("* Something's wrong"),
} : {});

const TargetingPreferencesInternal = ({
  data,
  error,
  onDataChange,
  isDateSettingsDisplayed,
}) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);

  const [adScheduleView, setAdScheduleView] = useState(null);
  const setAdScheduleViewCallback = useCallback((view) => {
    setAdScheduleView(view);
  }, []);

  const [optimizedTargetingEnabled, setOptimizedTargetingEnabled] = useState(false);
  const [isDeviceCampaignToggleVisible, setIsDeviceCampaignToggleVisible] = useState(false);

  const { state } = useWizardService();

  const { flow, isDefaultAdGroupStepModel, adGroup: initialAdGroup } = state.context.wizardData;

  const { adGroup, campaign = {}, audienceList } = data || {};

  const { advancedOptions } = useMemo(() => getAdGroupPanels(flow, serverContext), [flow, serverContext]);
  const isCTVFlow = useMemo(() => isCTVCampaign(flow), [flow]);
  const isAdGroupEditFlow = useMemo(() => isAudienceAdGroupEdit(flow), [flow]);

  const currentDate = useMemo(() => new Date(), []);

  const {
    DynamicPermissions: {
      XandrNegativeTarget,
      EnableMSANDisplayCampaigns,
      EnableMSANVideoCampaigns,
      EnableKeywordCustomSegments,
      InMarketAudienceRedesign,
      BoostConversionBasedSegment,
      ContentTargetingMSNSubcategories,
    },
    Permissions: {
      IsAmplifyEnabled,
      IsAIMCampaignEnabled,
    },
  } = serverContext;

  const boostConversionBasedSegmentEnabled = IsAmplifyEnabled && BoostConversionBasedSegment;
  const {
    // custom segments will be a part of audience criteria
    audiences,
    interests,
    locationTargets,
    isTargetSetAudience,
    initialIndustryTargets,
    industry,
    negativeIndustry,
    isTargetSetIndustry,
    initialJobTargets,
    jobTargets,
    negativeJobTargets,
    isTargetSetJobFunction,
    companyNameTargets,
    negativeCompanyNameTargets,
    isTargetSetCompanyName,
    deviceTypes,
    BoostAdunitIds,
    customSegments,
    deviceTargetingCampaignLevel,
    campaignLevelDeviceTargets,
    placements,
  } = adGroup || {};

  const classes = useDefaultStyles(getAdGroupStyles);
  const [
    allAdGroupTargetingSections,
    adgroupTargetingExpandedSections,
    setAdGroupTargetingExpandedSections,
  ] = useCollapseState(
    advancedOptions /* all sections */,
    boostConversionBasedSegmentEnabled ? ['audience-target'] : [] /* initially expanded sections */
  );

  const urlOptions = useMemo(() => ({
    TrackingUrlTemplate: (adGroup && adGroup.TrackingUrlTemplate) || null,
    FinalUrlSuffix: (adGroup && adGroup.FinalUrlSuffix) || null,
    UrlCustomParameters: (adGroup && adGroup.UrlCustomParameters) || null,
  }), [adGroup]);
  const urlOptionsDetails = useMemo(() => getUrlOptionsDetails(i18n, urlOptions), [urlOptions, i18n]);

  const webExclusionsError = _.filter(error, err => err.property === 'webExclusions');

  const setExclusionsCallback = useCallback((exclusionsData, type) => {
    if (data) {
      const patchedData = {
        data: {
          ...data,
          exclusions: {
            ...(data.exclusions || {}),
            adgroupExclusions: {
              ...(data.exclusions && data.exclusions.adgroupExclusions ? data.exclusions.adgroupExclusions : {}),
              [type]: exclusionsData,
            },
          },
        },
      };

      onDataChange(patchedData);
    }
  }, [data, onDataChange]);

  const currentLocationTargets = useMemo(
    () => {
      if (!locationTargets) {
        return {};
      }
      return (locationTargets.useParentLevel ? locationTargets.parentLevelSettings : locationTargets.currentLevelSettings);
    },
    [locationTargets]
  );

  const getCampaignSubType = useMemo(() => {
    if (isDisplayAdsCampaign(flow)) {
      return CampaignSubTypes.DisplayAds;
    } else if (isAudience(flow)) {
      return CampaignSubTypes.AudienceStatic;
    }
    return CampaignSubTypes.BrandAwarenessVideoAds;
  }, [flow]);

  const isAdScheduleCampaignToggleVisible = useMemo(() => {
    if (isAdGroupCreation(flow) || isAudienceAdGroupEdit(flow)) {
      return false;
    }
    return true;
  }, [flow]);

  const shouldSelectAllDevices = useMemo(() => {
    if (campaign != null && adGroup != null) {
      if (campaign.CampaignType === 'Audience'
        && adGroup.initialDeviceTypes
        && adGroup.initialDeviceTypes.length === 0) {
        return true;
      }
    }

    return false;
  }, [campaign, adGroup]);

  const campaignGoal = useMemo(() => {
    const goal = _.result(
      _.find(campaign.CampaignSettings, i => _.has(i, 'CampaignObjectiveSettingType')),
      'CampaignObjectiveSettingType'
    );
    return goal;
  }, [campaign]);

  const adScheduleDetails = useMemo(() => {
    const adScheduleDetailsMessage = getAdScheduleDetails(i18n, adScheduleView && adScheduleView.current, true);

    if (isDateSettingsDisplayed && adGroup) {
      return `${getDateSettingDetails(i18n, adGroup.StartDate, adGroup.EndDate)}: ${adScheduleDetailsMessage}`;
    }

    return adScheduleDetailsMessage;
  }, [adGroup, adScheduleView, i18n, isDateSettingsDisplayed]);

  const supportExclusion = isAudience(flow) ? true : XandrNegativeTarget;

  useEffect(() => {
    if (deviceTargetingCampaignLevel || !_.isEmpty(campaignLevelDeviceTargets)) {
      setIsDeviceCampaignToggleVisible(true);
    }
  }, [deviceTargetingCampaignLevel, campaignLevelDeviceTargets]);

  const adGroupTargetingSections = () => (
    <Collapse allPanelItems={allAdGroupTargetingSections} checkItems skipItemsTypeCheck activeKeys={adgroupTargetingExpandedSections} setActiveKeys={setAdGroupTargetingExpandedSections}>
      {InMarketAudienceRedesign && (
      <ValidatedPanel
        key="interests-target"
        title={<TextTitle
          titleKey={_TL_('In-market audiences')}
          key="interests-target"
          topicKey="pop_BA_Audiences_IMA"
          panelKey="interests-target"
          details={formatInterests({ interests, i18n })}
        />}
      >
        { interests ? (
          <ValidatedAudienceInterestsWithBidAdjustment
            showBidAdjustments={isAudienceAdGroupEdit(flow)}
            supportExclusion={supportExclusion}
            validationProps={{
              data: {
                interests,
              },
            }}
            isAIMCampaignEnabled={IsAIMCampaignEnabled}
            campaignType={campaign.CampaignType}
            audienceList={audienceList}
            locationTargets={currentLocationTargets}
            autoTargeting={adGroup.UseOptimizedTargeting}
            campaignBiddingStrategy={(campaign && BidStrategy[campaign.BiddingScheme.Type])}
            campaignSubType={getCampaignSubType}
          />) : (<SectionShimmer />)
          }
      </ValidatedPanel>
        )}
      <ValidatedPanel
        key="audience-target"
        title={<TextTitle
          titleKey={_TL_('Audience')}
          key="audience-target"
          topicKey="pop_BA_CampaignCreate_AudTargets_Audience"
          panelKey="audience-target"
          details={formatAudience({ audiences, i18n, InMarketAudienceRedesign })}
        />}
      >
        {
            audiences ? (
              <ValidatedAudiencePickerWithBidAdjustment
                i18n={i18n}
                campaignSubType={getCampaignSubType}
                campaignGoal={campaignGoal}
                supportExclusion={supportExclusion}
                isAudienceCreationWorkflow={!isCTVFlow && (initialAdGroup === null || (initialAdGroup && initialAdGroup.StartDate > currentDate) || isDefaultAdGroupStepModel)}
                hideCustomSegmentsOption={initialAdGroup === null || (initialAdGroup && initialAdGroup.StartDate > currentDate) || isDefaultAdGroupStepModel || isAdGroupEditFlow}
                validationProps={{
                  data: {
                    audiences,
                  },
                }}
                showBidAdjustments={isAudienceAdGroupEdit(flow)}
                isIMARedesignEnabled={InMarketAudienceRedesign}
                boostConversionBasedSegmentEnabled={boostConversionBasedSegmentEnabled}
              />
            ) : (<SectionShimmer />)
          }
        {isAudienceAdGroupEdit(flow) &&
        <ValidatedTargetOrBidSelector
          styles={{ marginTop: 32 }}
          field="isTargetSetAudience"
          value={isTargetSetAudience}
          targets={audiences}
        />
          }
      </ValidatedPanel>
      <ValidatedPanel
        key="device-target"
        title={<TextTitle
          titleKey={_TL_('Device')}
          key="device-target"
          panelKey="device-target"
          topicKey={isCTVFlow ? 'pop_BA_PremiumStreaming_DeviceTargeting' : 'pop_BA_CampaignCreate_AudTargets_Device'}
          details={formatDevices({ devices: deviceTargetingCampaignLevel ? campaignLevelDeviceTargets : deviceTypes, i18n, isCTVSupported: isCTVFlow })}
        />}
      >
        {
            (deviceTypes && campaignLevelDeviceTargets) ? (
              <>
                {
                  isDeviceCampaignToggleVisible && <ValidatedLevelSwitch
                    validationProps={{
                      schema: {
                        type: 'object',
                        properties: {
                          deviceTargetingCampaignLevel: {
                            type: 'boolean',
                          },
                        },
                      },
                      field: 'deviceTargetingCampaignLevel',
                    }}
                  />
                }
                <div className={classNames(classes.pickerContainer, !deviceTargetingCampaignLevel && classes.hide)}>
                  <Device
                    i18n={i18n}
                    devices={campaignLevelDeviceTargets}
                    deviceRestrictions={[DeviceTypes.Computers, DeviceTypes.Smartphones, DeviceTypes.Tablets]}
                    isCTVSupported={false}
                    validationPropsField="campaignLevelDeviceTargets"
                    key="campaignLevelDeviceTargets"
                  />
                  {isAudienceAdGroupEdit(flow) && (
                    <ValidatedBidAdjustment
                      data={data}
                      validationProps={{
                            data: data.adGroup,
                            stopPropagation: true,
                          }}
                      criterionType="Device"
                      criterionLevel="CampaignCriterion"
                      readOnly
                      isAudienceEdit={isAudienceAdGroupEdit(flow)}
                    />
                      )
                    }
                </div>
                <div className={classNames(classes.pickerContainer, deviceTargetingCampaignLevel && classes.hide)}>
                  <Device
                    i18n={i18n}
                    devices={deviceTypes}
                    isCTVSupported={isCTVFlow}
                    validationPropsField="deviceTypes"
                    defaultSelectAll={shouldSelectAllDevices}
                    key="deviceTypes"
                  />
                  {
                        isAudienceAdGroupEdit(flow) && (
                          <ValidatedBidAdjustment
                            data={data}
                            validationProps={{
                              data: data.adGroup,
                              stopPropagation: true,
                            }}
                            criterionType="Device"
                            isAudienceEdit={isAudienceAdGroupEdit(flow)}
                          />
                        )
                      }
                </div>
              </>
            ) : (<SectionShimmer />)
          }
      </ValidatedPanel>
      <ValidatedPanel
        key="company-target"
        title={<TextTitle
          titleKey={_TL_('Company')}
          key="company-target"
          panelKey="company-target"
          topicKey="pop_BA_CampaignCreate_AudTargets_Company"
          details={formatCompany({ companyNameTargets, i18n })}
        />}
      >
        <ValidatedCompanyPicker
          classes={classes}
          campaignSubType={getCampaignSubType}
          showBidAdjustments={isAudienceAdGroupEdit(flow)}
          validationProps={{
            data: {
              companyNameTargets,
              negativeCompanyNameTargets,
            },
          }}
        />
        {isAudienceAdGroupEdit(flow) &&
        <ValidatedTargetOrBidSelector
          field="isTargetSetCompanyName"
          value={isTargetSetCompanyName}
          targets={companyNameTargets}
          negativeTargets={negativeCompanyNameTargets}
          styles={{ marginTop: 8 }}
        />
        }
      </ValidatedPanel>
      <ValidatedPanel
        key="industry-target"
        title={<TextTitle
          titleKey={_TL_('Industry')}
          key="industry-target"
          topicKey="pop_BA_CampaignCreate_AudTargets_Industry"
          panelKey="industry-target"
          validationProps={{
            data: {
              industry,
              negativeIndustry,
            },
          }}
          details={formatIndustry({ industry, i18n })}
        />}
      >
        <ValidatedIndustryPicker
          classes={classes}
          campaignSubType={getCampaignSubType}
          showBidAdjustments={isAudienceAdGroupEdit(flow)}
          initialIndustryTargets={initialIndustryTargets}
        />
        {isAudienceAdGroupEdit(flow) &&
        <ValidatedTargetOrBidSelector
          field="isTargetSetIndustry"
          value={isTargetSetIndustry}
          targets={industry}
          negativeTargets={negativeIndustry}
          styles={{ marginTop: 8 }}
        />
        }
      </ValidatedPanel>
      <ValidatedPanel
        key="job-target"
        title={<TextTitle
          titleKey={_TL_('Job function')}
          key="job-target"
          topicKey="pop_BA_CampaignCreate_AudTargets_JobFunction"
          panelKey="job-target"
          details={formatJobFunction({ jobTargets, i18n })}
        />}
      >
        <ValidatedJobFunctionPicker
          classes={classes}
          campaignSubType={getCampaignSubType}
          validationProps={{
            data: {
              jobTargets,
              negativeJobTargets,
            },
          }}
          showBidAdjustments={isAudienceAdGroupEdit(flow)}
          initialJobTargets={initialJobTargets}
        />
        {isAudienceAdGroupEdit(flow) &&
        <ValidatedTargetOrBidSelector
          field="isTargetSetJobFunction"
          value={isTargetSetJobFunction}
          targets={jobTargets}
          negativeTargets={negativeJobTargets}
        />
        }
      </ValidatedPanel>
      {EnableKeywordCustomSegments && customSegments &&
      <ValidatedPanel
        key="custom-segments"
        title={<TextTitle
          titleKey={_TL_('Custom segments')}
          key="custom-segments"
          topicKey="pop_BA_Audiences_CustomSegments"
          panelKey="custom-segments"
          details={formatCustomSegment({ customSegments, i18n })}
          headingRole="heading"
          headingLevel="2"
        />}
        noOverflowHidden
      >
        <ValidatedCustomSegmentsPicker
          i18n={i18n}
          validationProps={{
            data: customSegments,
          }}
        />
      </ValidatedPanel>
      }
      <ValidatedPanel
        key="content-targeting-placements"
        title={<TextTitle
          titleKey={_TL_('Placements')}
          key="content-targeting-placements"
          topicKey="pop_BA_ContentTargeting_Placements"
          panelKey="content-targeting-placements"
          details={getContentTargetingPlacementsSummaryText({ i18n, placements })}
          noOverflowHidden
        />}
      >
        <ContentTargetingPlacements
          msnSubcategoriesEnabled={ContentTargetingMSNSubcategories}
          validationProps={{
          data: {
            placements: placements || [],
          },
        }}
        />
      </ValidatedPanel>
      <ValidatedPanel
        key="placements-1P-target"
        title={<TextTitle
          titleKey={_TL_('Placements')}
          key="placements-1P-target"
          topicKey="pop_BA_AdPlacements"
          panelKey="placements-1P-target"
          details={formatPlacements({ BoostAdunitIds, i18n })}
        />}
      >
        <ValidatedPlacementsFunctionPicker
          validationProps={{
            data: {
              BoostAdunitIds,
            },
          }}
        />
      </ValidatedPanel>
      <ValidatedPanel
        key="advanced-url-options"
        title={<TextTitle titleKey={_TL_('Advanced URL options')} details={urlOptionsDetails} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <span className="url-options-descriptions">
          {i18n.getString(_TL_('Set up the tracking and redirect information for your ads.'))}
        </span>
        {
          adGroup &&
          <ValidatedUrlOptionsParameter
            i18n={i18n}
            validationProps={{
              data: {
                TrackingUrlTemplate: adGroup.TrackingUrlTemplate,
                FinalUrlSuffix: adGroup.FinalUrlSuffix,
                UrlCustomParameters: adGroup.UrlCustomParameters,
              },
            }}
          />
        }
      </ValidatedPanel>
      <ValidatedPanel
        key="ad-rotation"
        title={
          <TextTitle
            titleKey={_TL_('Ad rotation')}
            details={adGroup && ROTATION_OPTION_TO_TEXT[adGroup.AdRotation]}
          />
        }
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <span className="ad-rotation-descriptions">
          {i18n.getString(_TL_('Which ads do you want to show?'))}
        </span>
        {
          adGroup &&
          <ValidatedAdRotation
            field="AdRotation"
            value={adGroup.AdRotation}
          />
        }
      </ValidatedPanel>
      <ValidatedPanel
        key="ad-schedule"
        title={adGroup ? <TextTitle
          titleKey={_TL_('Ad schedule')}
          key="ad-schedule"
          topicKey="pop_BA_Targeting_DayTime"
          panelKey="ad-schedule"
          details={adScheduleDetails}
        /> : <SectionShimmer count={1} />}
      >
        {
          adGroup ?
            <AdSchedule
              adGroup={adGroup}
              campaign={campaign}
              isCreate={initialAdGroup === null || (initialAdGroup && initialAdGroup.StartDate > currentDate) || isDefaultAdGroupStepModel}
              hideUseCampaignSetting={isAdScheduleCampaignToggleVisible}
              setAdScheduleViewCallback={setAdScheduleViewCallback}
              isDateSettingsDisplayed={isDateSettingsDisplayed}
            />
            : (<SectionShimmer />)
        }
      </ValidatedPanel>
      <ValidatedPanel
        key="web-exclusions"
        title={<TextTitle titleKey={_TL_('Web exclusions')} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <span className="web-exclusions-description">
          {i18n.getString(_TL_('Which websites should not show your ads and which IP addresses should be blocked from seeing your ads?'))}
        </span>
        <ServerValidatedWebExclusions
          showSiteExclusion
          level={ScopeLevel.AdGroup}
          exclusionData={data && data.exclusions ? data.exclusions : {}}
          setExclusionsCallback={setExclusionsCallback}
          errors={webExclusionsError}
          formErrorProps={{ filter: webExclusionPropertyVal }}
        />
      </ValidatedPanel>
      <ValidatedFixedPanel
        key="predictive-targeting-control"
        title={data ? (
          <PredictiveTargetingControlHeader
            validationProps={{
              data: adGroup,
            }}
            onToggleEnabledChanged={setOptimizedTargetingEnabled}
            campaignSubType={getCampaignSubType}
            alwaysDisabled={IsAmplifyEnabled && BoostConversionBasedSegment && campaignGoal === CampaignObjectiveType.Acquisition}
            EnableMSANDisplayCampaigns={EnableMSANDisplayCampaigns}
            EnableMSANVideoCampaigns={EnableMSANVideoCampaigns}
          />)
          : <SectionShimmer count={1} />
        }
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <MessageBar intent="info" layout="auto">
          <MessageBarBody>
            {!optimizedTargetingEnabled ?
              i18n.getString(_TL_('Predictive targeting is automatically enabled. You can disable it by setting a targeting preference.'))
              : i18n.getString(_TL_('Turning off predictive targeting significantly limits our ability to help you reach the best audience for your campaign.'))
            }
          </MessageBarBody>
        </MessageBar>
      </ValidatedFixedPanel>
    </Collapse>
  );

  return (
    <>
      <ValidatedBootstrapPanel
        defaultExpanded={boostConversionBasedSegmentEnabled} // expand for the boost scenario
        className={classNames(classes.collapsiblePanel, 'targeting-preferences-panel')}
        render={() => (
          <>
            <BootstrapPanel.Heading>
              <BootstrapPanel.Title toggle>
                {i18n.getString(_TL_('Targeting preferences (optional)'))}
              </BootstrapPanel.Title>
            </BootstrapPanel.Heading>
            <BootstrapPanel.Collapse>
              <BootstrapPanel.Body>
                <div className={classNames('targeting-preferences-section')}>
                  {adGroupTargetingSections()}
                </div>
              </BootstrapPanel.Body>
            </BootstrapPanel.Collapse>
          </>
        )}
      />
    </>
  );
};

TargetingPreferencesInternal.propTypes = {
  data: PropTypes.shape({
    locationTargets: PropTypes.shape({}),
    adGroup: PropTypes.shape({
      locationTargets: PropTypes.shape({}),
      deviceTypes: PropTypes.shape({}),
    }),
    errors: PropTypes.arrayOf(PropTypes.shape({
      message: PropTypes.string,
      property: PropTypes.string,
      code: PropTypes.string,
    })),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string,
    property: PropTypes.string,
    code: PropTypes.string,
  })).isRequired,
  isDateSettingsDisplayed: PropTypes.bool,
};

TargetingPreferencesInternal.defaultProps = {
  isDateSettingsDisplayed: true,
};

export const TargetingPreferences = TargetingPreferencesInternal;
